import type { PhoneMessage, PlanPeriod, PlanTier, WebMessage } from '@tellvo/setup-protocol';
import type { Key } from './i18n/index.js';
import { WebSession, type Connection, type SessionPhase } from './session.js';
import { isChange, Syncer, type View } from './sync.js';

export interface CheckResult { target: string; registration: string; reason?: string; log: string[] }

/** One line under the header: what the phone said, or what it waits for. */
export type Notice =
  | { tone: 'success' | 'bad'; key: Key }
  | { tone: 'bad'; text: string }
  | { tone: 'warn'; key: Key; waiting: true };

export interface SetupState {
  phase: SessionPhase;
  /** The last phase the page was working in, drawn behind the lost-connection window. */
  live: SessionPhase;
  notice: Notice | null;
  check: CheckResult | null;
  checking: boolean;
  log: string[] | null;
  /** A new pairing code is being fetched after the old one expired. */
  renewing: boolean;
  /** The page's own link to the relay; the console shows it in the header and under it. */
  connection: Connection;
}

/**
 * The page's state outside React: the session to the relay, the change model on top of the
 * phone's snapshots, and what the phone last answered. Components read it through
 * `useSyncExternalStore` and act through its methods.
 */
export class SetupStore {
  private state: SetupState = { phase: { kind: 'connecting' }, live: { kind: 'connecting' }, notice: null, check: null, checking: false, log: null, renewing: false, connection: 'ok' };
  private listeners = new Set<() => void>();
  readonly session: WebSession;
  readonly syncer: Syncer;

  constructor(relayURL: string, origin: string) {
    this.session = new WebSession(relayURL, origin, {
      phase: (phase) => this.onPhase(phase),
      message: (message) => this.onPhone(message),
      connection: (connection) => this.set({ connection }),
    });
    this.syncer = new Syncer(this.session);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SetupState => this.state;

  get view(): View | null {
    return this.syncer.model.view();
  }

  get online(): boolean {
    return this.state.phase.kind === 'paired';
  }

  phoneHas(id: string): boolean {
    return this.syncer.model.phoneHas(id);
  }

  start(): void {
    void this.session.start();
  }

  // MARK: actions

  allow(): void { this.session.confirmPhone(); }
  /** The pairing code ran out on this page's clock. */
  expire(): void { this.session.expire(); }
  /** "Retry" after the page stopped trying to reach the relay on its own. */
  retry(): void { this.session.retryNow(); }
  /** A new code in place of the expired one, without reloading the page. */
  renew(): void {
    if (this.state.renewing) return;
    this.set({ renewing: true });
    void this.session.renew();
  }
  reject(): void { this.session.rejectPhone(); }
  bye(): void { this.session.bye(); }

  /** Live messages (preview, check, log) and changes alike; changes are kept until confirmed. */
  send(message: WebMessage): void {
    this.syncer.send(message);
    // A change the phone has not got yet: ask the relay to wake it (rate-limited there).
    if (isChange(message) && this.state.phase.kind === 'away') this.session.wake();
    // A change shows at once, before the phone has it.
    this.set({});
  }

  save(account: Record<string, unknown>, password: string | undefined, isNew: boolean): void {
    this.send({ kind: 'upsert', account, ...(password !== undefined ? { password } : {}) });
    if (!this.online) this.set({ notice: { tone: 'success', key: 'notice.savedHere' }, check: null });
    else if (!isNew && password !== undefined) this.set({ notice: { tone: 'warn', key: 'notice.confirmPassword', waiting: true }, check: null });
    else this.set({ notice: null, check: null });
  }

  /** The plans on the iPhone, on this tier and period (#13); the phone answers whether it could. */
  openPlans(tier: PlanTier, period: PlanPeriod): void {
    this.send({ kind: 'openPlans', tier, period });
  }

  remove(id: string): void {
    if (!this.phoneHas(id)) {
      this.syncer.model.forget(id);
      this.set({ notice: null });
      return;
    }
    this.send({ kind: 'delete', accountId: id });
    this.set({ notice: this.online
      ? { tone: 'warn', key: 'notice.confirmDelete', waiting: true }
      : { tone: 'success', key: 'notice.deleteLater' } });
  }

  check(account: Record<string, unknown>, password: string | undefined): void {
    if (!this.online || this.state.checking) return;
    this.send({ kind: 'check', account, ...(password !== undefined ? { password } : {}) });
    this.set({ checking: true, check: null });
  }

  requestLog(): void {
    if (this.online) this.send({ kind: 'log' });
  }

  /** The last answer is old news once the person moves on; a wait for the phone stays. */
  clearNotice(): void {
    if (this.state.notice && !('waiting' in this.state.notice)) this.set({ notice: null });
  }

  // MARK: events

  private onPhase(phase: SessionPhase): void {
    // Paired again (or for the first time): the phone gets every change it has not confirmed.
    if (phase.kind === 'paired') this.syncer.flush();
    // Gone with changes still unconfirmed: they need it back.
    if (phase.kind === 'away' && (this.syncer.model.view()?.pending ?? 0) > 0) this.session.wake();
    const live = phase.kind === 'error' ? this.state.live : phase;
    this.set({ phase, live, checking: phase.kind === 'paired' ? this.state.checking : false, renewing: (phase.kind === 'expired' || phase.kind === 'offline') && this.state.renewing });
  }

  private onPhone(message: PhoneMessage): void {
    this.syncer.onPhone(message);
    switch (message.kind) {
      case 'snapshot': this.set({}); return;
      case 'status': this.set({ check: message, checking: false }); return;
      case 'log': this.set({ log: message.lines }); return;
      case 'saved': this.set({ notice: { tone: 'success', key: 'notice.saved' } }); return;
      case 'confirmed':
        if (message.action === 'openPlans') {
          this.set({ notice: message.ok ? { tone: 'success', key: 'notice.plansOpen' } : { tone: 'bad', key: 'notice.plansClosed' } });
          return;
        }
        this.set({ notice: message.ok
          ? { tone: 'success', key: message.action === 'delete' ? 'notice.deleted' : 'notice.saved' }
          : { tone: 'bad', key: 'notice.rejected' } });
        return;
      case 'error': this.set({ notice: { tone: 'bad', text: message.message } }); return;
      case 'bye': this.set({ phase: { kind: 'error', reason: 'bye' } }); return;
    }
  }

  private set(patch: Partial<SetupState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}
